import { cn } from '../../lib/utils';

export interface UnseenIndicatorProps {
  /**
   * Accessible label announced to screen readers. The dot is the sole visual
   * carrier of "there are changes you haven't looked at yet", so it always
   * announces itself rather than being decorative.
   */
  label?: string;
  className?: string;
  /** Overrides the rendered `data-testid`. Defaults to `unseen-indicator`. */
  testId?: string;
}

/**
 * A small violet dot that marks an item with changes the viewer has not looked
 * at yet — the Google-Docs "new activity since you last opened it" tell.
 *
 * The color is deliberately violet rather than the brand green: green already
 * reads as "connected / working" on the status icons, so an unread cue needs a
 * hue no agent status uses. `--status-accent-violet` carries its own light/dark
 * values, so the dot stays legible on both surfaces.
 *
 * Two stacked spans give it depth without shouting: a soft blurred halo that
 * breathes (gated behind `motion-safe` so it holds still under
 * `prefers-reduced-motion`) and a crisp dot with a faint inner ring. It is
 * deliberately steadier than a `StatusDot` pulse — this signals "unread", not
 * "live".
 */
export function UnseenIndicator({
  label = 'Unseen changes',
  className,
  testId = 'unseen-indicator',
}: UnseenIndicatorProps) {
  return (
    <span
      role="status"
      aria-label={label}
      data-testid={testId}
      className={cn('relative inline-flex size-1.5 shrink-0', className)}
    >
      <span
        aria-hidden
        className="absolute -inset-1 rounded-full bg-status-accent-violet/30 blur-[1.5px] motion-safe:animate-pulse"
      />
      <span
        aria-hidden
        className="relative inline-flex size-full rounded-full bg-status-accent-violet ring-1 ring-inset ring-black/10 dark:ring-white/25"
      />
    </span>
  );
}
